import { Field, Form, Formik } from 'formik';
import React, { useRef } from 'react';

import { PrimaryButton, ProButton, TextButton } from '@components/Button/variants';
import SearchIcon from '@components/Images/SearchIcon';
import useEventTracking from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { deep_research_path, search_page_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { WIDTH_SM } from '@utils/constants';
import { cleanQueryForClickText } from '@utils/tracking';

const DEEP_RESEARCH_SEARCH_BUTTON_LABEL = 'Deep Research Search Submit';
const REGULAR_SEARCH_BUTTON_LABEL = 'Search Submit';

interface SearchBarProps {
  isDeepResearchActive?: boolean;
  searchQuery?: string;
  setSearchQuery: (searchQuery: string) => void;
  scope: string;
  title?: string;
}

export default function SearchBar({
  isDeepResearchActive = false,
  searchQuery,
  setSearchQuery,
  scope,
  title,
}: SearchBarProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const SearchButton = isDeepResearchActive ? ProButton : PrimaryButton;
  const buttonLabel = isDeepResearchActive
    ? DEEP_RESEARCH_SEARCH_BUTTON_LABEL
    : REGULAR_SEARCH_BUTTON_LABEL;

  const { width: windowWidth } = useWindowSize();

  const { trackEvent } = useEventTracking({});

  const handleSubmit = (values: { query: string; scope: string }) => {
    // Build the URL with query parameters
    const basePath = isDeepResearchActive ? deep_research_path() : search_page_path();
    const cleanedQuery = cleanQueryForClickText(values.query);
    const searchParams = new URLSearchParams({
      query: cleanedQuery,
    });
    if (!isDeepResearchActive) searchParams.set('scope', values.scope);

    const newLink = `${basePath}?${searchParams.toString()}`;

    // Track the search event
    trackEvent(
      click({
        clickTarget: isDeepResearchActive
          ? ClickTarget.DeepResearchSubmit
          : ClickTarget.RegularSearchSubmit,
        clickText: cleanedQuery,
        clickLink: newLink,
      })
    );

    // Navigate to the search results page
    window.location.href = newLink;
  };

  return (
    <div>
      <Formik
        initialValues={{
          query: searchQuery || '',
          scope: scope,
        }}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form ref={formRef} id="form_id" data-id="search-form">
            <div className="flex w-full flex-col gap-12 sm:flex-row sm:items-center sm:justify-between sm:gap-56">
              <div className="relative w-full">
                <Field
                  innerRef={inputRef}
                  type="text"
                  name="query"
                  className="text-grayscale-black h-56 w-full rounded-none border-0 border-b border-grayscale-charcoal py-2 pl-0 pr-80 font-suisse-screen text-headline-sm placeholder:text-grayscale-gray/50 focus:outline-none"
                  placeholder={isDeepResearchActive ? 'Ask Deep Research' : 'Search'}
                  autoFocus
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.value;
                    setFieldValue('query', newValue);
                    setSearchQuery(newValue);
                  }}
                />
                {values.query && (
                  <TextButton
                    as="button"
                    type="button"
                    onClick={() => {
                      setFieldValue('query', '');
                      setSearchQuery('');
                      inputRef.current?.focus();
                    }}
                    className="!absolute right-32 top-1/2 !w-fit -translate-y-1/2 p-0 font-suisse-intl-mono text-overline-sm font-normal !text-grayscale-gray !no-underline"
                    aria-label="Clear search"
                  >
                    clear
                  </TextButton>
                )}
                <SearchIcon className="absolute right-2 top-1/2 h-20 w-20 -translate-y-1/2" />
              </div>
              <SearchButton
                as="button"
                aria-label={buttonLabel}
                className="w-full px-36 sm:w-auto sm:self-end"
                type="submit"
                size={windowWidth < WIDTH_SM ? 'small' : 'big'}
              >
                {isDeepResearchActive ? 'Deep Research' : 'Search'}
              </SearchButton>
            </div>
            <Field type="hidden" name="scope" />
          </Form>
        )}
      </Formik>
      {title && (
        <div className="mt-12 font-suisse-intl-mono text-overline-md text-primary-black">
          Results for &lsquo;{title}&rsquo;
        </div>
        // TODO: Pull num_results from Algolia
        // {@link https://app.asana.com/1/1200145886478/project/1201413338509198/task/1209940023501323?focus=true}
        // <div className="mt-12 font-suisse-intl-mono text-overline-md text-primary-black">
        //   Your search for &lsquo;{title}&rsquo; returned {num_results} results
        // </div>
      )}
    </div>
  );
}
